// src/components/ImageSlider.jsx
import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { Link } from "react-router-dom";

const FALLBACK = "/images/work1.jpg"; // 預設圖片，避免首頁空白

export default function ImageSlider() {
  const [images, setImages] = useState([FALLBACK]); // 只存 URL 陣列，維持你原本結構
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  // 📌 小工具：預載圖片（成功或失敗都 resolve，避免卡死）
  const preload = (src) =>
    new Promise((resolve) => {
      if (!src) return resolve();
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  // 讀取 Supabase 的 slider_images（只會取啟用的，並依排序/建立時間）
  useEffect(() => {
    let stop = false;
    (async () => {
      const { data, error } = await supabase
        .from("slider_images")
        .select("image_url")
        .order("sort_order", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true });

      if (stop) return;
      if (error) {
        console.warn("[ImageSlider] fetch error:", error);
        setImages([FALLBACK]);
        setIndex(0);
        return;
      }

      const urls = (data || []).map((r) => r.image_url).filter(Boolean);

      if (urls.length === 0) {
        setImages([FALLBACK]);
      } else {
        setImages(urls);
      }
      setIndex(0);
    })();

    return () => {
      stop = true;
    };
  }, []);

  // 自動輪播（沿用你的 8 秒），圖片數量變動時重設
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (images.length <= 1) return; // 只有一張就不自動輪播
    timerRef.current = setInterval(async () => {
      const next = (indexRef.current + 1) % images.length;
      await preload(images[next]);
      setIndex(next);
      indexRef.current = next;
    }, 8000);
    return () => clearInterval(timerRef.current);
  }, [images, images.length]);

  // 📌 讓 setInterval 能讀到最新 index
  const indexRef = useRef(index);
  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  const prev = async () => {
    const nextIdx = (indexRef.current - 1 + images.length) % images.length;
    await preload(images[nextIdx]);
    setIndex(nextIdx);
    indexRef.current = nextIdx;
  };
  const next = async () => {
    const nextIdx = (indexRef.current + 1) % images.length;
    await preload(images[nextIdx]);
    setIndex(nextIdx);
    indexRef.current = nextIdx;
  };
  return (
    <>
      {/* 置中的「門診時刻表」按鈕 */}
      <div className="slider-header">
        <Link
          to="/schedule"
          className="slider-cta-btn"
          aria-label="前往門診時刻表頁面"
        >
          門診時刻表
        </Link>
        <Link
          to="/visitprogress"
          class="slider-cta-btn slider-cta-btn--red"
          aria-label="前往看診進度頁面"
        >
          目前看診進度
        </Link>
      </div>
      <div className="slider">
        {images.map((src, i) => (
          <div
            key={`${src}-${i}`}
            className={`slide ${i === index ? "active" : ""}`}
          >
            <img
              src={src}
              alt={`院長作品 ${i + 1}`}
              className="slide-img"
              loading={i === index ? "eager" : "lazy"} // 📌 當前張用 eager，其他仍 lazy
              decoding="async"
              onError={(e) => (e.currentTarget.src = FALLBACK)}
            />
          </div>
        ))}

        {/* 左右切換按鈕（保留原樣式與位置） */}
        <button className="prev" onClick={prev} aria-label="上一張">
          ❮
        </button>
        <button className="next" onClick={next} aria-label="下一張">
          ❯
        </button>
      </div>
    </>
  );
}
